import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GalleryCard from "@/components/GalleryCard";
import { generateImage, getImagesCount, listImages } from "@/lib/api";

const SAMPLE_PROMPTS = [
  "A neon-lit kyoto alley at midnight, photoreal, light rain",
  "Hyperdetailed macro of a glass beetle, studio lighting",
  "Brutalist concrete library on Mars, surreal, 35mm film",
  "A retro-future poster of an analog space station, risograph style",
];

export default function Home() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("gemini");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [data, count] = await Promise.all([listImages(), getImagesCount()]);
      setImages(data);
      setTotal(count);
    } catch (err) {
      console.error("Failed to load gallery:", err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onGenerate = async (e) => {
    e?.preventDefault();
    const p = prompt.trim();
    if (p.length < 2) {
      toast.error("Add a prompt first");
      return;
    }
    setBusy(true);
    try {
      const created = await generateImage({ prompt: p, model });
      toast.success("Image generated", { description: created.id });
      setPrompt("");
      // Optimistically prepend
      setImages((prev) => [created, ...prev]);
      setTotal((t) => t + 1);
      // Optional: navigate to detail
      navigate(`/image/${created.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error("Generation failed", { description: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO / PROMPT */}
      <section className="border-b-2 border-black grain">
        <div className="px-6 md:px-12 pt-10 md:pt-16 pb-10 md:pb-12 max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="label-eyebrow text-muted-foreground mb-3">
                ◼ FORGE / IMAGE LAB / V1
              </div>
              <h1
                data-testid="hero-title"
                className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.85]"
              >
                Type a thought.
                <br />
                <span className="text-[#FF3333]">Forge</span> an image.
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg font-sans text-muted-foreground">
                A public gallery of AI-generated images. Every image gets its
                own permanent URL — share it, link it, embed it.
              </p>
            </div>
            <div className="hidden md:flex flex-col items-end gap-2">
              <span className="label-eyebrow">Total works</span>
              <span
                data-testid="image-count"
                className="font-display text-6xl tracking-tighter"
              >
                {String(total).padStart(3, "0")}
              </span>
            </div>
          </div>

          <form onSubmit={onGenerate}>
            <div className="border-2 border-black bg-white">
              <textarea
                data-testid="prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                disabled={busy}
                placeholder="Describe the image you want to forge…"
                className="w-full p-5 md:p-6 text-xl md:text-2xl font-sans bg-transparent border-0 focus:ring-0 focus:outline-none resize-none placeholder:text-muted-foreground disabled:opacity-60"
              />
              <div className="flex flex-col md:flex-row md:items-stretch border-t-2 border-black">
                <div className="flex items-center gap-2 px-4 md:px-6 py-3 border-b-2 md:border-b-0 md:border-r-2 border-black">
                  <span className="label-eyebrow text-muted-foreground">
                    Model
                  </span>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger
                      data-testid="model-select"
                      className="rounded-none border-0 shadow-none focus:ring-0 h-10 font-mono uppercase tracking-wider text-sm w-[230px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-black">
                      <SelectItem value="gemini" data-testid="model-gemini">
                        Gemini Nano-Banana
                      </SelectItem>
                      <SelectItem value="openai" data-testid="model-openai">
                        OpenAI GPT-Image-1
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 flex items-center gap-2 px-4 md:px-6 py-3 overflow-x-auto">
                  <span className="label-eyebrow text-muted-foreground shrink-0">
                    Try
                  </span>
                  {SAMPLE_PROMPTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`sample-prompt-${s.slice(0, 10)}`}
                      onClick={() => setPrompt(s)}
                      className="shrink-0 label-eyebrow px-3 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
                    >
                      {s.split(",")[0].slice(0, 28)}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  data-testid="generate-button"
                  disabled={busy}
                  className="flex items-center justify-center gap-3 px-8 md:px-10 py-5 bg-[#FF3333] text-white font-display uppercase tracking-widest text-base md:text-lg border-t-2 md:border-t-0 md:border-l-2 border-black hover:bg-black transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {busy ? (
                    <span className="font-mono blink">GENERATING</span>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Forge
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* GALLERY */}
      <section className="px-6 md:px-12 py-10 md:py-16 max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between mb-8 border-b-2 border-black pb-4">
          <div>
            <div className="label-eyebrow text-muted-foreground">
              The collection
            </div>
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
              Gallery
            </h2>
          </div>
          <div className="label-eyebrow text-muted-foreground hidden md:block">
            Hover a piece for actions
          </div>
        </div>

        {loadingList ? (
          <div
            data-testid="loading-gallery"
            className="font-mono text-sm blink"
          >
            LOADING ARCHIVE
          </div>
        ) : images.length === 0 ? (
          <div
            data-testid="empty-gallery"
            className="border-2 border-dashed border-black p-12 text-center"
          >
            <div className="font-display text-2xl uppercase mb-2">
              No works yet
            </div>
            <p className="font-mono text-sm text-muted-foreground">
              Forge the first image to seed the gallery.
            </p>
          </div>
        ) : (
          <div
            data-testid="gallery-grid"
            className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6"
          >
            {images.map((img, i) => (
              <GalleryCard key={img.id} image={img} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
